import { createListLinkTarget } from '../../utils/listPageState'

const EDIT_APP_RANKING_BANNER_DRAFT_PREFIX = 'admin:appRankingBannerDraft:edit:'
export const NEW_APP_RANKING_BANNER_DRAFT_KEY = 'admin:appRankingBannerDraft:new'
export const APP_RANKING_BANNER_IMAGE_FILE_ERROR_MESSAGE = '이미지 파일만 업로드할 수 있습니다.'
export const APP_RANKING_BANNER_IMAGE_FILE_ACCEPT = '.jpg,.jpeg,.png,.gif,.webp,image/png,image/gif,image/jpeg,image/webp'
export const APP_RANKING_BANNER_IMAGE_SECTION_CONFIGS = {
    edit: [
        {
            fieldName: 'image',
            inputId: 'image',
            previewAlt: '메인이미지',
            uploadLabel: '이미지 업로드',
            cardMaxWidth: 500,
        },
    ],
    create: [
        {
            fieldName: 'image',
            inputId: 'image',
            previewAlt: '이미지',
            uploadLabel: '이미지 업로드',
            cardMaxWidth: 600,
        },
    ],
}

const getSessionStorage = () => {
    if (typeof window === 'undefined') {
        return null
    }

    return window.sessionStorage
}

const normalizeTextValue = (value) => {
    if (value === undefined || value === null) {
        return ''
    }

    return String(value)
}

const normalizeNumberValue = (value, fallbackValue) => {
    if (value === undefined || value === null || value === '') {
        return fallbackValue
    }

    const parsedValue = Number(value)

    return Number.isNaN(parsedValue) ? fallbackValue : parsedValue
}

// 구분·노출 여부는 카드 선택이라 숫자로 고정한다.
// 순위는 직접 입력이라 지우는 중(빈 값)을 그대로 둬야 한다. 숫자로 바꾸면 지우자마자 0이 돼서 다시 채워진다.
const APP_RANKING_BANNER_NUMBER_FIELDS = ['type', 'status']

export const APP_RANKING_BANNER_REWARD_TYPES = {
    NONE: 'none',
    POINT: 'point',
    GIFTICON: 'gifticon',
}

// 노출 국가. 보여주는 대상과 받는 대상이 같다(가입 국가 kr이면 국내, 그 외는 해외).
export const APP_RANKING_BANNER_EXPOSE_REGIONS = {
    ALL: 'all',
    KR: 'kr',
    GLOBAL: 'global',
}

export const APP_RANKING_BANNER_EXPOSE_REGION_OPTIONS = [
    {
        value: APP_RANKING_BANNER_EXPOSE_REGIONS.ALL,
        title: '국내 + 해외',
        description: '모든 유저에게 보여주고, 순위에 든 유저에게 지급합니다.',
    },
    {
        value: APP_RANKING_BANNER_EXPOSE_REGIONS.KR,
        title: '국내',
        description: '가입 국가가 한국인 유저에게만 보여주고 지급합니다. 기프티콘은 국내로 두세요.',
    },
    {
        value: APP_RANKING_BANNER_EXPOSE_REGIONS.GLOBAL,
        title: '해외',
        description: '가입 국가가 한국이 아닌 유저에게만 보여주고 지급합니다.',
    },
]

export const APP_RANKING_BANNER_EXPOSE_REGION_LABELS = {
    all: '국내+해외',
    kr: '국내',
    global: '해외',
}

// 지급 규칙(포인트·기프티콘)은 적용 기간이 필수다. 전시만은 비워도 된다.
export const isPaidAppRankingBannerReward = (rewardType) => (
    rewardType === APP_RANKING_BANNER_REWARD_TYPES.POINT
    || rewardType === APP_RANKING_BANNER_REWARD_TYPES.GIFTICON
)

const isAppRankingBannerDate = (value) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ''))) {
        return false
    }

    const [year, month, day] = String(value).split('-').map(Number)
    const date = new Date(Date.UTC(year, month - 1, day))

    return date.getUTCFullYear() === year && date.getUTCMonth() + 1 === month && date.getUTCDate() === day
}

// 서버가 고른 날짜를 기간 단위로 맞춰 저장한다(주간 월요일~일요일, 월간 1일~말일).
export const getAppRankingBannerPeriodUnitLabel = (type) => (
    Number(type) === 2 || Number(type) === 4 ? '매월 1일 ~ 말일' : '월요일 ~ 일요일'
)

const APP_RANKING_BANNER_TEXT_DRAFT_FIELDS = [
    'title',
    'url',
    'rank',
    'rankTo',
    'rewardType',
    'pointAmount',
    'goodsCode',
    'exposeRegion',
    'applyFrom',
    'applyTo',
]

export const createDefaultAppRankingBannerParams = () => ({
    type: 1,
    rank: 1,
    rankTo: '',
    title: '',
    url: '',
    status: 1,
    rewardType: APP_RANKING_BANNER_REWARD_TYPES.NONE,
    pointAmount: '',
    goodsCode: '',
    exposeRegion: APP_RANKING_BANNER_EXPOSE_REGIONS.ALL,
    applyFrom: '',
    applyTo: '',
    // 기프티콘 상품 이미지를 서버가 받아와 쓴다(직접 업로드 대신).
    useGoodsImage: false,
})

// App 랭킹경품 폼은 이미지 파일을 제외한 선택/텍스트 필드만 sessionStorage에 남긴다.
export const createAppRankingBannerDraftPayload = ({ params = {} } = {}) => {
    const textParams = APP_RANKING_BANNER_TEXT_DRAFT_FIELDS.reduce((acc, field) => {
        if (field === 'rewardType') {
            acc[field] = normalizeTextValue(params.rewardType) || APP_RANKING_BANNER_REWARD_TYPES.NONE
        } else if (field === 'exposeRegion') {
            acc[field] = normalizeTextValue(params.exposeRegion) || APP_RANKING_BANNER_EXPOSE_REGIONS.ALL
        } else {
            acc[field] = normalizeTextValue(params[field])
        }
        return acc
    }, {})

    return {
        params: {
            type: normalizeNumberValue(params.type, 1),
            status: normalizeNumberValue(params.status, 1),
            useGoodsImage: Boolean(params.useGoodsImage),
            ...textParams,
        }
    }
}

// 서버 기본값 위에 draft만 덮어써서 새로고침 뒤에도 랭킹경품 작성 흐름을 이어간다.
export const applyAppRankingBannerDraft = ({ baseParams = {}, draft }) => {
    const normalizedBaseDraft = createAppRankingBannerDraftPayload({
        params: baseParams,
    })

    if (!draft) {
        return {
            params: {
                ...baseParams,
                ...normalizedBaseDraft.params,
            },
        }
    }

    const draftParams = draft.params || {}
    const textParams = APP_RANKING_BANNER_TEXT_DRAFT_FIELDS.reduce((acc, field) => {
        acc[field] = draftParams[field] === undefined
            ? normalizedBaseDraft.params[field]
            : normalizeTextValue(draftParams[field])
        return acc
    }, {})

    return {
        params: {
            ...baseParams,
            type: draftParams.type === undefined
                ? normalizedBaseDraft.params.type
                : normalizeNumberValue(draftParams.type, normalizedBaseDraft.params.type),
            status: draftParams.status === undefined
                ? normalizedBaseDraft.params.status
                : normalizeNumberValue(draftParams.status, normalizedBaseDraft.params.status),
            useGoodsImage: draftParams.useGoodsImage === undefined
                ? normalizedBaseDraft.params.useGoodsImage
                : Boolean(draftParams.useGoodsImage),
            ...textParams,
        }
    }
}

// 등록/수정 화면이 같은 숫자 필드 변환 규칙과 저장 검증을 사용하도록 정리한다.
export const applyAppRankingBannerFieldChange = ({ params = {}, name, value }) => {
    if (!name) {
        return params
    }

    const nextParams = {
        ...params,
        [name]: APP_RANKING_BANNER_NUMBER_FIELDS.includes(name)
            ? Number(value)
            : value,
    }

    // 경품 종류를 바꾸면 그 종류에서만 쓰는 값은 비운다(포인트 규칙에 상품코드가 남지 않게).
    if (name === 'rewardType') {
        if (value !== APP_RANKING_BANNER_REWARD_TYPES.POINT) {
            nextParams.pointAmount = ''
        }

        if (value !== APP_RANKING_BANNER_REWARD_TYPES.GIFTICON) {
            nextParams.goodsCode = ''
            nextParams.goodsName = ''
            nextParams.convertPoint = 0
            nextParams.useGoodsImage = false
        }
    }

    return nextParams
}

export const prepareAppRankingBannerSubmit = ({
    params = {},
    rankingBannerId,
    requireImage = true,
    titleRequiredMessage = '제목을 입력하세요.',
} = {}) => {
    const rewardType = normalizeTextValue(params.rewardType) || APP_RANKING_BANNER_REWARD_TYPES.NONE
    const nextParams = {
        ...params,
        title: normalizeTextValue(params.title).trim(),
        url: normalizeTextValue(params.url).trim(),
        rewardType,
        rank: normalizeTextValue(params.rank).trim(),
        rankTo: normalizeTextValue(params.rankTo).trim(),
        pointAmount: normalizeTextValue(params.pointAmount).trim(),
        goodsCode: normalizeTextValue(params.goodsCode).trim(),
        applyFrom: normalizeTextValue(params.applyFrom).trim(),
        applyTo: normalizeTextValue(params.applyTo).trim(),
        useGoodsImage: Boolean(params.useGoodsImage),
    }
    const fail = (errorMessage) => ({ nextParams, errorMessage })

    // 상품 이미지를 쓰면 파일을 올리지 않아도 된다(서버가 기프티쇼 상품 이미지를 가져온다).
    if (requireImage && !nextParams.image && !nextParams.useGoodsImage) {
        return fail('이미지를 업로드하거나 상품 이미지 사용을 선택하세요.')
    }

    if (nextParams.useGoodsImage && rewardType !== APP_RANKING_BANNER_REWARD_TYPES.GIFTICON) {
        return fail('상품 이미지는 기프티콘 경품에서만 사용할 수 있습니다.')
    }

    if (!nextParams.title) {
        return fail(titleRequiredMessage)
    }

    if (!nextParams.rank || Number(nextParams.rank) < 1) {
        return fail('순위를 1 이상으로 입력하세요.')
    }

    if (nextParams.rankTo && Number(nextParams.rankTo) < Number(nextParams.rank)) {
        return fail('끝 순위를 시작 순위 이상으로 입력하세요.')
    }

    if (rewardType === APP_RANKING_BANNER_REWARD_TYPES.POINT && !(Number(nextParams.pointAmount) > 0)) {
        return fail('지급 포인트를 1 이상으로 입력하세요.')
    }

    if (rewardType === APP_RANKING_BANNER_REWARD_TYPES.GIFTICON && !nextParams.goodsCode) {
        return fail('기프티콘 상품을 선택하세요.')
    }

    if (isPaidAppRankingBannerReward(rewardType) && (!nextParams.applyFrom || !nextParams.applyTo)) {
        return fail('지급 규칙은 적용 기간 시작과 끝을 모두 지정하세요.')
    }

    if (Boolean(nextParams.applyFrom) !== Boolean(nextParams.applyTo)) {
        return fail('적용 기간은 시작과 끝을 함께 지정하거나 함께 비워야 합니다.')
    }

    if (nextParams.applyFrom) {
        if (!isAppRankingBannerDate(nextParams.applyFrom) || !isAppRankingBannerDate(nextParams.applyTo)) {
            return fail('적용 기간 날짜가 올바르지 않습니다.')
        }

        if (nextParams.applyFrom > nextParams.applyTo) {
            return fail('적용 기간 시작이 끝보다 늦습니다.')
        }
    }

    const formData = new FormData()

    if (rankingBannerId !== undefined && rankingBannerId !== null && rankingBannerId !== '') {
        formData.append('rankingBannerId', rankingBannerId)
    }

    if (nextParams.image) {
        formData.append('image', nextParams.image)
    }

    formData.append('type', nextParams.type)
    formData.append('rank', nextParams.rank)
    formData.append('rankTo', nextParams.rankTo)
    formData.append('title', nextParams.title)
    formData.append('url', nextParams.url)
    formData.append('status', nextParams.status)
    formData.append('rewardType', rewardType)
    formData.append('pointAmount', nextParams.pointAmount)
    formData.append('goodsCode', nextParams.goodsCode)
    formData.append('exposeRegion', normalizeTextValue(params.exposeRegion) || APP_RANKING_BANNER_EXPOSE_REGIONS.ALL)
    formData.append('applyFrom', nextParams.applyFrom)
    formData.append('applyTo', nextParams.applyTo)

    if (nextParams.useGoodsImage) {
        formData.append('useGoodsImage', '1')
    }

    return {
        nextParams,
        formData,
        errorMessage: '',
    }
}

export const createAppRankingBannerListLinkTarget = ({
    listSearch = '',
    snackbarState,
    curPage,
    searchText,
    searchSelect,
}) => createListLinkTarget({
    pathname: '/appRankingBanners',
    search: listSearch,
    ...(snackbarState ? {
        openSnackbar: true,
        severitySnackbar: snackbarState.severity,
        messageSnackbar: snackbarState.message,
    } : {}),
    curPage,
    searchText,
    searchSelect,
})

// 목록 복귀 시 기존 필터/페이지 상태를 유지하는 규칙을 한 곳에서 맞춘다.
export const moveToAppRankingBannerList = ({
    draftKey,
    clearDraft,
    setNavigationUnlocked,
    history,
    listSearch,
    snackbarState,
    curPage,
    searchText,
    searchSelect,
}) => {
    setNavigationUnlocked(true)
    clearDraft(draftKey)

    setTimeout(() => {
        history.push(createAppRankingBannerListLinkTarget({
            listSearch,
            snackbarState,
            curPage,
            searchText,
            searchSelect,
        }))
    }, 0)
}

export const handleAppRankingBannerBackNavigation = ({
    event,
    leaveGuardEnabled,
    draftKey,
    clearDraft,
    confirmLeave,
    moveToList,
}) => {
    if (!leaveGuardEnabled) {
        clearDraft(draftKey)
        return
    }

    event.preventDefault()

    if (!confirmLeave()) {
        return
    }

    moveToList()
}

const isAppRankingBannerImageFile = (file) => {
    return Boolean(file) && (
        (file.type && file.type.startsWith('image/')) ||
        /\.(jpg|jpeg|png|gif|webp)$/i.test(file.name || '')
    )
}

export const handleAppRankingBannerImageUpload = ({
    event,
    setParams,
    setImage,
    onInvalidFile,
}) => {
    event.preventDefault()

    const input = event.target
    const file = input.files?.[0]

    if (!file) {
        return
    }

    if (!isAppRankingBannerImageFile(file)) {
        input.value = ''
        onInvalidFile(APP_RANKING_BANNER_IMAGE_FILE_ERROR_MESSAGE)
        return
    }

    const reader = new FileReader()

    reader.onloadend = () => {
        setParams((prev) => ({
            ...prev,
            [input.name]: file,
        }))

        setImage(reader.result)
    }

    reader.readAsDataURL(file)
}

export const createEditAppRankingBannerDraftKey = (rankingBannerId) => `${EDIT_APP_RANKING_BANNER_DRAFT_PREFIX}${rankingBannerId}`

export const readAppRankingBannerDraft = (draftKey) => {
    const storage = getSessionStorage()

    if (!storage || !draftKey) {
        return null
    }

    try {
        const storedValue = storage.getItem(draftKey)
        return storedValue ? JSON.parse(storedValue) : null
    } catch (_error) {
        return null
    }
}

export const saveAppRankingBannerDraft = (draftKey, draftValue) => {
    const storage = getSessionStorage()

    if (!storage || !draftKey) {
        return
    }

    try {
        storage.setItem(draftKey, JSON.stringify(draftValue))
    } catch (_error) {
        // sessionStorage가 막혀 있어도 작성 흐름은 그대로 유지한다.
    }
}

export const clearAppRankingBannerDraft = (draftKey) => {
    const storage = getSessionStorage()

    if (!storage || !draftKey) {
        return
    }

    try {
        storage.removeItem(draftKey)
    } catch (_error) {
        // storage 정리에 실패해도 화면 동작은 계속 진행한다.
    }
}
